import {useEffect, useState} from 'react';
import {Moment} from 'moment';

import {fuzzyMatchSanitized, matchPartsFromSegments} from 'common/util/fuzzy-match';
import {DEFAULT_USER_NAME} from 'common/report/util/profile/types';
import {epochTimestampToProjectTimeZone} from 'common/util/format-property';
import {Translatable} from 'common/i18n';
import {formatDuration} from 'common/util/date';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {renameEvent} from 'common/report/util/events';
import {getProjectId} from 'common/report/util';
import {appApiUrl} from 'common/report/queries/utils';
import {apiClient} from 'common/util/api-client';

import {PAGE_VIEW_EVENT, REPLAY_EVENT, sessionRecordingQueryUtils, TRACKED_REPLAY_ID_PREFIX} from './query-utils';
import {
  EmbedReplayMessage,
  EmbedReplayPayload,
  isReplayWindowMessage,
  ParentAppMessagePayload,
  ParentAppReplayMessage,
  RecordingEventMetadata,
  RecordingEvents,
  RecordingMetadata,
  RecordingPageViewEventMetadata,
  ReplayMessage,
  SignedReplayParams,
  TimeDisplaySetting,
  WindowMessageRecipient,
} from './types';

export async function fetchSignedReplayParams(replayId: string, replayEnv: string): Promise<SignedReplayParams> {
  const url = appApiUrl(`replays/sign`, {projectId: getProjectId()});
  return apiClient.post<SignedReplayParams>(url, {replay_id: replayId, replay_env: replayEnv});
}

export function formatRecordingEventTime(replayTime: number, eventTime: number): string {
  const seconds = new Date((eventTime - replayTime) * 1000);
  return seconds.toISOString().substring(11, 19);
}

export function formatRecordingProgressTimeBar(recordingProgress: number, recordingDuration: number) {
  let progress = new Date(recordingProgress);
  const duration = new Date(recordingDuration);

  if (progress > duration) {
    progress = duration;
  }

  let timeDisplaySetting = TimeDisplaySetting.Hours;
  if (duration.getUTCHours() === 0) {
    if (duration.getUTCMinutes() === 0) {
      timeDisplaySetting = TimeDisplaySetting.Seconds;
    } else {
      timeDisplaySetting = TimeDisplaySetting.Minutes;
    }
  }
  return {
    formattedProgress: formatRecordingProgressTime(progress, timeDisplaySetting),
    formattedDuration: formatRecordingProgressTime(duration, timeDisplaySetting),
  };
}

function formatRecordingProgressTime(date: Date, setting: TimeDisplaySetting) {
  if (isNaN(date.getTime())) {
    return `???`;
  }
  if (setting === TimeDisplaySetting.Hours) {
    return date.toISOString().substring(11, 19);
  } else if (setting === TimeDisplaySetting.Minutes) {
    return date.toISOString().substring(14, 19);
  } else {
    return date.toISOString().substring(15, 19);
  }
}

export function getDisplayName(email: string, name: string, distinctId: string): string {
  if (name && name !== DEFAULT_USER_NAME) {
    return name;
  }

  if (email) {
    return email;
  }

  return distinctId;
}

export function getDisplayDate(secondsFromEpoch: number, timezone: string | null): Translatable {
  if (!secondsFromEpoch || !timezone || isNaN(new Date(secondsFromEpoch * 1000).getTime())) {
    return ({t}) => t(`session-replay.unknown-date`);
  }

  return ({formatDate}) =>
    formatDate(epochTimestampToProjectTimeZone(secondsFromEpoch * 1000, timezone) as Moment, {
      year: `numeric`,
      month: `short`,
      day: `numeric`,
      timeZone: timezone || undefined,
    });
}

export function getDisplayTime(secondsFromEpoch: number, timezone: string | null): Translatable {
  if (!secondsFromEpoch || !timezone || isNaN(new Date(secondsFromEpoch * 1000).getTime())) {
    return ({t}) => t(`session-replay.unknown-time`);
  }

  return ({formatDate}) =>
    formatDate(epochTimestampToProjectTimeZone(secondsFromEpoch * 1000, timezone) as Moment, {
      hour: `numeric`,
      minute: `numeric`,
      hour12: true,
      timeZone: timezone || undefined,
    });
}

export function getFormattedDuration(duration: number): Translatable {
  if (!duration) {
    return ({t}) => t(`session-replay.unknown-duration`);
  }

  const formattedDuration = formatDuration(duration, 0);

  return `${formattedDuration.value}${formattedDuration.unit[0]}`;
}

export function getEventCount(eventCount: number, eventsLoading: boolean): Translatable {
  if (eventsLoading) {
    return ({t}) => t(`session-replay.loading-events`);
  }
  return ({t}) => t(`session-replay.events-count`, {count: eventCount});
}

// Hook used to fetch url metadata for a list of replays based on page view events that happened during those replays
export function useReplayPageViewEvents(replays: RecordingMetadata[]) {
  const [replaysPageViewEvents, setReplaysPageViewEvents] = useState<Map<string, RecordingPageViewEventMetadata[]>>(
    new Map(),
  );
  const [replayPageViewEventsLoaded, setReplayPageViewEventsLoaded] = useState<boolean>(false);

  async function fetchPageViewEventsForReplays(currentReplays: RecordingMetadata[]) {
    if (currentReplays.length > 0) {
      const queryResult = await sessionRecordingQueryUtils.fetchPageViewsForReplays(currentReplays);

      const newRecordingsPageViewEvents = new Map<string, RecordingPageViewEventMetadata[]>();
      for (const replay of currentReplays) {
        if (replay.startingURL) {
          newRecordingsPageViewEvents.set(replay.replayId, [{currentURL: replay.startingURL, eventTime: replay.time}]);
        }
      }

      queryResult.forEach((result) => {
        const currentValue = newRecordingsPageViewEvents.get(result.replayId) || [];

        newRecordingsPageViewEvents.set(result.replayId, currentValue.concat(result.replayPageViewEvents));
      });
      setReplaysPageViewEvents(newRecordingsPageViewEvents);
      setReplayPageViewEventsLoaded(true);
    } else {
      setReplayPageViewEventsLoaded(true);
    }
  }

  function getReplayPageViewEventsForId(replayId: string) {
    if (!replayId || !replaysPageViewEvents.has(replayId)) {
      return [];
    }

    return replaysPageViewEvents.get(replayId);
  }

  function hasReplayPageViewEvents() {
    return Array.from(replaysPageViewEvents.entries()).some((entry) => entry[1].length > 0);
  }

  useEffect(() => {
    if (replays.length > 0) {
      setReplayPageViewEventsLoaded(false);
      fetchPageViewEventsForReplays(replays);
    }
  }, [replays]);

  return {replayPageViewEventsLoaded, getReplayPageViewEventsForId, hasReplayPageViewEvents};
}

export function useReplayEvents(
  replays: RecordingMetadata[],
  selectedReplay: RecordingMetadata | null,
  initialEvents: RecordingEvents[] | null,
) {
  const [recordingsGeneralEventMetadata, setRecordingsGeneralEventMetadata] = useState<
    Map<string, RecordingEventMetadata[]>
  >(new Map());
  const [recordingsSpecificEventMetadata, setRecordingsSpecificEventMetadata] = useState<
    Map<string, RecordingEventMetadata[]>
  >(new Map());
  const [recordingsInitialEventMetadata, setRecordingsInitialEventMetadata] = useState<
    Map<string, RecordingEventMetadata[]>
  >(new Map());
  const [recordingsEventMetadataLoading, setRecordingsEventMetadataLoading] = useState<boolean>(false);
  const currentProject = useCurrentProject();

  async function fetchEventMetadataForReplays(currentReplays: RecordingMetadata[]) {
    if (currentReplays.length > 0) {
      const queryResult = await sessionRecordingQueryUtils.fetchEventsForReplays(
        currentReplays,
        currentProject.get().value,
      );

      const newRecordingsGeneralEventMetadata = new Map(recordingsGeneralEventMetadata);
      queryResult.forEach((result) => {
        newRecordingsGeneralEventMetadata.set(result.replayId, result.replayEvents);
      });
      setRecordingsGeneralEventMetadata(newRecordingsGeneralEventMetadata);
      setRecordingsEventMetadataLoading(false);
    } else {
      setRecordingsEventMetadataLoading(false);
    }
  }

  async function fetchEventMetadataForReplay(replay: RecordingMetadata) {
    const queryResults = await sessionRecordingQueryUtils.fetchEventsForReplay(replay, currentProject.get().value);

    const newRecordingsSpecificEventMetadata = new Map(recordingsSpecificEventMetadata);
    newRecordingsSpecificEventMetadata.set(replay.replayId, queryResults.replayEvents);
    setRecordingsSpecificEventMetadata(newRecordingsSpecificEventMetadata);
  }

  function hasReplayPageViewEventsFromEventsQuery() {
    return Array.from(recordingsSpecificEventMetadata.entries()).some((entry) =>
      entry[1].some((event) => event.eventName === PAGE_VIEW_EVENT),
    );
  }

  function formatEvents(events: RecordingEventMetadata[]) {
    // filter out checkpoint events and rename to lexicon display names
    // TODO: will filtering out checkpoint events at the query level help perf / cardinality limit?
    return events
      .filter((event) => event.eventName !== REPLAY_EVENT)
      .map((event) => ({...event, eventName: renameEvent(event.eventName)}));
  }

  function getReplayEventsForId(replayId: string) {
    if (!replayId) {
      return [];
    }

    if (recordingsSpecificEventMetadata.has(replayId)) {
      return formatEvents(recordingsSpecificEventMetadata.get(replayId) ?? []);
    }

    const eventsToReturn = recordingsGeneralEventMetadata.get(replayId) || [];
    const initialEventsForReplay = recordingsInitialEventMetadata.get(replayId) || [];

    initialEventsForReplay.forEach((event) => {
      if (
        !eventsToReturn.some((existingEvent) => {
          return existingEvent.eventName === event.eventName && existingEvent.eventTime === event.eventTime;
        })
      ) {
        eventsToReturn.push(event);
      }
    });

    return formatEvents(eventsToReturn);
  }

  useEffect(() => {
    if (replays.length > 0) {
      setRecordingsEventMetadataLoading(true);
      fetchEventMetadataForReplays(replays);
    }
  }, [replays]);

  useEffect(() => {
    if (selectedReplay) {
      fetchEventMetadataForReplay(selectedReplay);
    }
  }, [selectedReplay]);

  useEffect(() => {
    if (initialEvents === null) {
      return;
    }

    const newRecordingsInitialEventMetadata = new Map(recordingsInitialEventMetadata);

    initialEvents.forEach((entry) => {
      newRecordingsInitialEventMetadata.set(entry.replayId.replace(TRACKED_REPLAY_ID_PREFIX, ``), entry.replayEvents);
    });
    setRecordingsInitialEventMetadata(newRecordingsInitialEventMetadata);
  }, [initialEvents]);

  return {recordingsEventMetadataLoading, getReplayEventsForId, hasReplayPageViewEventsFromEventsQuery};
}

export function getSearchHighlightPieces(
  inputText: string,
  searchFilter: string,
): Array<{text: string; highlight: boolean}> {
  if (!inputText) return [{text: inputText, highlight: false}];

  const match = fuzzyMatchSanitized(inputText.toLowerCase(), searchFilter.toLowerCase());
  if (searchFilter && match) {
    const matchParts = matchPartsFromSegments(inputText, match.segments);

    return matchParts.map((value, i) => {
      return {text: value, highlight: i % 2 === 0};
    });
  } else {
    return [{text: inputText, highlight: false}];
  }
}

export function sendEmbedReplayMessage(payload: EmbedReplayPayload, win: Window) {
  const message: EmbedReplayMessage = {payload, recipient: WindowMessageRecipient.ChildReplay};

  let origin;
  if (REPLAY_FROM_SAME_ORIGIN) {
    // for development environments, both embedded and parent origin should be the devbox
    origin = window.location.origin;
  } else {
    // otherwise, the origin is our CDN
    origin = `https://cdn.mxpnl.com`;
  }

  win.postMessage(message, origin);
}

// targetOrigin here is a variable since postMessage won't support multiple origins, and we need to be able to
// send messages to eu.mixpanel.com, staging-1.mixpanel.com etc.
// the EmbeddedReplay component won't be emitting anything senstive, but for best practice we'll let the parent app
// send down the current origin, and the child will check that against a whitelist and use that.
export function sendParentReplayMessage(payload: ParentAppMessagePayload, targetOrigin: string, win: Window) {
  const message: ParentAppReplayMessage = {payload, recipient: WindowMessageRecipient.ParentApp};

  let origin = targetOrigin;
  if (REPLAY_FROM_SAME_ORIGIN) {
    // for development environments, both embedded and parent origin should be the devbox
    origin = window.location.origin;
  }

  win.postMessage(message, origin);
}

export function useReplayWindowMessageListener<MessageType extends ReplayMessage>(
  recipient: MessageType[`recipient`],
  win: Window,
  cb: (message: MessageType) => void,
) {
  useEffect(() => {
    function handleMessage(event: MessageEvent<MessageType>) {
      if (isReplayWindowMessage(event) && event.data.recipient === recipient) {
        cb(event.data);
      }
    }

    win.addEventListener(`message`, handleMessage);

    return () => {
      win.removeEventListener(`message`, handleMessage);
    };
  });
}
